import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getProfilePhotoUrl, getLogoUrl } from '../config';
import logger from '../utils/logger';

const ProServiceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Filtre période pour revenus
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [showCustomPeriod, setShowCustomPeriod] = useState(false);
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Calculer les dates de période
  const getPeriodDates = useCallback((period) => {
    const today = new Date();
    let dateDebut = null;
    let dateFin = today.toISOString().split('T')[0];
    
    switch (period) {
      case 'today':
        dateDebut = dateFin;
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        dateDebut = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        dateDebut = monthStart.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        dateDebut = quarterStart.toISOString().split('T')[0];
        break;
      case 'year':
        dateDebut = `${today.getFullYear()}-01-01`;
        break;
      case 'custom':
        if (customDateStart && customDateEnd) {
          dateDebut = customDateStart;
          dateFin = customDateEnd;
        }
        break;
      default:
        return { dateDebut: null, dateFin: null };
    }
    
    return { dateDebut, dateFin };
  }, [customDateStart, customDateEnd]);

  // Charger les stats avec période
  const loadStats = useCallback(async () => {
    try {
      const { dateDebut, dateFin } = getPeriodDates(periodFilter);
      let url = '/service-requests/pro/stats';
      if (dateDebut && dateFin) {
        url += `?date_debut=${dateDebut}&date_fin=${dateFin}`;
      }
      const statsRes = await api.get(url);
      setStats(statsRes.data);
    } catch (err) {
      logger.error(err);
    }
  }, [periodFilter, getPeriodDates]);

  useEffect(() => {
    if (!user || (user.role !== 'author' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Recharger stats quand la période change
  useEffect(() => {
    if (user && (user.role === 'author' || user.role === 'admin')) {
      loadStats();
    }
  }, [periodFilter, customDateStart, customDateEnd, user, loadStats]);

  const loadData = async () => {
    try {
      const [requestsRes, statusesRes, statsRes] = await Promise.all([
        api.get('/service-requests/pro'),
        api.get('/service-statuses'),
        api.get('/service-requests/pro/stats')
      ]);
      setRequests(Array.isArray(requestsRes.data) ? requestsRes.data : []);
      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
      setStats(statsRes.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage et tri
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(r =>
        r.titre?.toLowerCase().includes(searchLower) ||
        r.user_nom?.toLowerCase().includes(searchLower) ||
        r.user_email?.toLowerCase().includes(searchLower) ||
        r.service_titre?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.statut_nom === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(r => r.priorite === priorityFilter);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.date_demande) - new Date(a.date_demande);
      if (sortBy === 'oldest') return new Date(a.date_demande) - new Date(b.date_demande);
      if (sortBy === 'priority') {
        const order = { 'urgente': 0, 'haute': 1, 'normale': 2, 'basse': 3 };
        return (order[a.priorite] || 4) - (order[b.priorite] || 4);
      }
      return 0;
    });

    return filtered;
  }, [requests, search, statusFilter, priorityFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, priorityFilter]);

  const getStatusBadge = (statut) => {
    const defaultColors = {
      'en_attente': 'bg-yellow-100 text-yellow-700',
      'en_cours': 'bg-blue-100 text-blue-700',
      'paiement_attente': 'bg-orange-100 text-orange-700',
      'paiement_recu': 'bg-teal-100 text-teal-700',
      'fourniture': 'bg-purple-100 text-purple-700',
      'termine': 'bg-green-100 text-green-700',
      'annule': 'bg-gray-100 text-gray-700',
      'refuse': 'bg-red-100 text-red-700'
    };
    return defaultColors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityIcon = (priorite) => {
    const icons = { 'basse': '🟢', 'normale': '🟡', 'haute': '🟠', 'urgente': '🔴' };
    return icons[priorite] || '⚪';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-orange-600 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">📨 Demandes reçues</h1>
            <p className="text-gray-600 mt-1">Gérez les demandes de vos clients pour vos services</p>
          </div>
          <Link
            to="/services"
            className="mt-4 md:mt-0 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            ← Retour aux services
          </Link>
        </div>

        {/* Stats Cards */}
        {stats && (
          <>
            {/* Sélecteur de période pour les revenus */}
            <div className="bg-white rounded-xl shadow p-4 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-gray-700 font-medium">📅 Période des revenus :</span>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'Tout' },
                    { value: 'today', label: "Aujourd'hui" },
                    { value: 'week', label: '7 derniers jours' },
                    { value: 'month', label: 'Ce mois' },
                    { value: 'quarter', label: 'Ce trimestre' },
                    { value: 'year', label: 'Cette année' },
                    { value: 'custom', label: 'Personnalisé' }
                  ].map(p => (
                    <button
                      key={p.value}
                      onClick={() => {
                        setPeriodFilter(p.value);
                        setShowCustomPeriod(p.value === 'custom');
                      }}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                        periodFilter === p.value 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Sélecteur de dates personnalisées */}
              {showCustomPeriod && (
                <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                  <span className="text-gray-600 text-sm">Du</span>
                  <input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    className="px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                  />
                  <span className="text-gray-600 text-sm">au</span>
                  <input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    className="px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                  />
                </div>
              )}
              
              {/* Afficher la période sélectionnée */}
              {stats.periode && (
                <div className="mt-2 text-sm text-gray-500">
                  📊 Données du {new Date(stats.periode.debut).toLocaleDateString('fr-FR')} au {new Date(stats.periode.fin).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div 
                className="bg-white rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition"
                onClick={() => setStatusFilter('all')}
              >
                <div className="text-3xl font-bold text-orange-600">{stats.total}</div>
                <div className="text-gray-500 text-sm">Total demandes</div>
              </div>
              <div 
                className="bg-white rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition"
                onClick={() => setStatusFilter('en_attente')}
              >
                <div className="text-3xl font-bold text-yellow-600">{stats.en_attente}</div>
                <div className="text-gray-500 text-sm">⏳ En attente</div>
              </div>
              <div className="bg-white rounded-xl shadow p-4 border-2 border-green-200">
                <div className="text-3xl font-bold text-green-600">{Number(stats.revenus || 0).toLocaleString()}</div>
                <div className="text-gray-500 text-sm">💰 Revenus (XOF)</div>
                {periodFilter !== 'all' && (
                  <div className="text-xs text-green-600 mt-1">
                    {periodFilter === 'custom' ? 'Période personnalisée' : 
                     periodFilter === 'today' ? "Aujourd'hui" :
                     periodFilter === 'week' ? '7 derniers jours' :
                     periodFilter === 'month' ? 'Ce mois' :
                     periodFilter === 'quarter' ? 'Ce trimestre' :
                     'Cette année'}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl shadow p-4 border-2 border-blue-200">
                <div className="text-3xl font-bold text-blue-600">{Number(stats.a_recevoir || 0).toLocaleString()}</div>
                <div className="text-gray-500 text-sm">📊 À recevoir (XOF)</div>
                {periodFilter !== 'all' && (
                  <div className="text-xs text-blue-600 mt-1">
                    {periodFilter === 'custom' ? 'Période personnalisée' : 
                     periodFilter === 'today' ? "Aujourd'hui" :
                     periodFilter === 'week' ? '7 derniers jours' :
                     periodFilter === 'month' ? 'Ce mois' :
                     periodFilter === 'quarter' ? 'Ce trimestre' :
                     'Cette année'}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <div className="text-3xl font-bold text-purple-600">
                  {requests.reduce((acc, r) => acc + (r.messages_non_lus || 0), 0)}
                </div>
                <div className="text-gray-500 text-sm">💬 Messages non lus</div>
              </div>
            </div>
          </>
        )}

        {/* Status Pills */}
        <div className="bg-white rounded-xl shadow p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                statusFilter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous ({requests.length})
            </button>
            {statuses.map(status => {
              const count = requests.filter(r => r.statut_nom === status.nom).length;
              return (
                <button
                  key={status.id}
                  onClick={() => setStatusFilter(status.nom)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                    statusFilter === status.nom 
                      ? 'bg-orange-600 text-white' 
                      : `${getStatusBadge(status.nom)} hover:opacity-80`
                  }`}
                >
                  {status.nom.replace('_', ' ')} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
              <input
                type="text"
                placeholder="Rechercher par nom, email, service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
            >
              <option value="all">Toutes priorités</option>
              <option value="urgente">🔴 Urgente</option>
              <option value="haute">🟠 Haute</option>
              <option value="normale">🟡 Normale</option>
              <option value="basse">🟢 Basse</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
            >
              <option value="recent">📅 Plus récentes</option>
              <option value="oldest">📆 Plus anciennes</option>
              <option value="priority">🎯 Par priorité</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-600 mb-4">
          <span className="font-bold text-orange-600">{filteredRequests.length}</span> demande(s)
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Demande</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorité</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedRequests.length > 0 ? (
                  paginatedRequests.map(request => (
                    <tr key={request.id} className={`hover:bg-gray-50 transition ${request.messages_non_lus > 0 ? 'bg-orange-50' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-gray-600">#{request.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 line-clamp-1">{request.titre}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{request.description?.substring(0, 50)}...</p>
                          </div>
                          {request.messages_non_lus > 0 && (
                            <span className="flex items-center justify-center min-w-[24px] h-6 px-2 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                              {request.messages_non_lus}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {request.photo_profil ? (
                              <img
                                src={getProfilePhotoUrl(request.photo_profil)}
                                alt={request.user_nom}
                                className="w-7 h-7 rounded-full object-cover border border-orange-200"
                              />
                            ) : (
                              <span className="inline-block w-7 h-7 bg-orange-100 rounded-full text-orange-600 font-semibold text-sm text-center leading-7">
                                {(request.user_nom?.[0] || 'U')}
                              </span>
                            )}
                            <span className="font-medium text-gray-800">{request.user_nom}</span>
                          </div>
                          <p className="text-xs text-gray-500">{request.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {request.logo && (
                            <img 
                              src={getLogoUrl(request.logo)}
                              alt=""
                              className="w-8 h-8 object-contain bg-gray-100 rounded"
                            />
                          )}
                          <span className="text-sm text-gray-700">{request.service_titre}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.statut_nom)}`}>
                          {request.statut_nom?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-lg">{getPriorityIcon(request.priorite)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {request.montant_final ? (
                          <div>
                            <p className="font-medium text-gray-800">{Number(request.montant_final).toLocaleString()}</p>
                            {request.montant_paye > 0 && (
                              <p className="text-xs text-green-600">Payé: {Number(request.montant_paye).toLocaleString()}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(request.date_demande)}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/pro/service-requests/${request.id}`}
                          className="px-3 py-1 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition text-sm font-medium"
                        >
                          Gérer →
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <span className="text-4xl block mb-2">📋</span>
                      <p className="text-gray-500">Aucune demande reçue</p>
                      <p className="text-gray-400 text-sm mt-1">Les demandes de vos clients apparaîtront ici</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white rounded-lg shadow hover:bg-gray-50 disabled:opacity-50"
            >
              ←
            </button>
            <span className="px-4 py-2 text-gray-600">
              Page {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white rounded-lg shadow hover:bg-gray-50 disabled:opacity-50"
            >
              →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProServiceRequests;
